import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { PanelFormCreateComponent } from './creation/panel-form-create/panel-form-create.component';
import type { NativeForm } from '../services/forms-api.service';
import { type BunchUpdateOptions } from '@modules/common/types/bunch-update-options';
import { Pagination2Component } from '@modules/common/components/pagination/pagination2.component';
import { ListSelectionBarComponent } from '@modules/common/components/list/list-selection-bar/list-selection-bar.component';
import { ListLoadErrorComponent } from '@modules/common/components/list/list-load-error/list-load-error.component';
import { CommonModule as AppCommonModule } from '@modules/common/common.module';
import { SelectableListDirective } from '@modules/drag-n-drop/directives/selectable-list.directive';
import { NavAddMenuComponent } from '@modules/pages/components/nav-add-menu/nav-add-menu.component';
import { ContextMenuStateIndicatorComponent } from '@frontendlib/components/features/popover/components/context-menu-state-indicator/context-menu-state-indicator.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { NoContentStubComponent } from '@frontendlib/components/components/no-content-stub/no-content-stub.component';
import { LoadingIndicatorComponent } from '@frontendlib/components/components/loading-indicator/loading-indicator.component';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { relativeDateToWorkDayStart } from '@modules/common/utils/date';
import { type FormFolderSummaryDto } from '@sharedlib/dtos/dto/forms-app/form.dto';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { Subject, firstValueFrom, map, of, type Observable } from 'rxjs';
import { LocalStorageItem } from '@frontendlib/common/decorators/local-storage.decorator';
import { heightAnimation } from '@modules/common/animations/height.animation';
import { FormsStitchService } from '../services/forms.service';
import { FormsSearchPopoverComponent } from './forms-search-popover.component';
import { SearchStateIndicatorComponent } from '@modules/search/components/search-state-indicator/search-state-indicator.component';
import { QuickActionButtonComponent } from '@modules/elements/components/quick-forms/quick-action-button/quick-action-button.component';
import { FormsFolderLibraryCardComponent } from './forms-folder-library-card.component';
import { FormFolderContextMenuDirective } from '../menus/form-folder-context-menu.directive';
import { FormsHubListComponent } from './forms-hub-list.component';
import { FormStitchFilters } from '../services/forms.service';
import { FormsLibraryCardComponent } from './forms-library-card.component';
import { FormsLibraryOptionsDirective } from '../menus/forms-library-options.directive';
import { FormsViewsContextMenuDirective } from '../menus/forms-views-context-menu.directive';
import { FormsQuickFormComponent } from './creation/forms-quick-form/forms-quick-form.component';
import { BoardsDialogService } from '@modules/boards/services/boards-dialog.service';
import { FormsCreationPickerComponent } from './creation/forms-creation-picker.component';
import { AccessLimitGateService } from '@modules/settings/services/access-limit-gate.service';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Constants } from '@frontendlib/common/types/Constants';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsPanelStateService } from '../services/forms-panel-state.service';
import { ActivatedRoute } from '@angular/router';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { SidebarItemComponent } from '@modules/common/components/sidebar-item/sidebar-item.component';
import { UtilsModule } from '@modules/utils/utils.module';
import { ElementsModule } from '@modules/elements/elements.module';
import { FormsUiSession } from '../agent/forms-ui-session';
import { FormsStitchyService } from '../agent/forms-stitchy.service';
import { formsUiHost } from '../services/forms-ui-host';
import { FormKnowledgeVisibilityService } from '../services/form-knowledge-visibility.service';
import { APPLICATION_SCOPE_TOKEN } from '@modules/common/types/ApplicationScope';
import { SplitViewService } from '@modules/split-view/services/split-view.service';
import { CollapsibleRailShellComponent } from '@modules/pages/components/collapsible-rail-shell/collapsible-rail-shell.component';
import { CollapsibleRailShellModule } from '@modules/pages/components/collapsible-rail-shell/collapsible-rail-shell.module';
import { SplitViewModule } from '@modules/split-view/split-view.module';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { isMainApplicationScope } from '@modules/common/types/ApplicationScope';
import { Application } from '@modules/common/types/application';
import { SidebarSplitViewKey } from '@modules/common/types/sidebar-split-view-key';
import { FullWindowService } from '@modules/common/services/full-window.service';
import { BaseAppStateService } from '@modules/common/services/base-app-state.service';
import { SEARCH_MY_SCOPE_LABEL } from '@modules/common/components/search-popup/search-my-scope-label.token';
import { FormsWorkspaceStore, type FormsView } from '../services/forms-workspace.store';
import { FormsRecordComponent } from './forms-record.component';
import { FormsFolderRecordComponent } from './forms-folder-record.component';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { FormsMainHandoffService, type FormsHandoff, type FormsHandoffReason } from '../services/forms-main-handoff.service';
import { FormsPanelNavigationService } from '../services/forms-panel-navigation.service';
import type { FormSummaryDto } from '@sharedlib/dtos/dto/forms-app/form.dto';
import { DragDataTypes, type DragData } from '@modules/drag-n-drop/types/drag-data';
import type { SelectableOpenEvent } from '@modules/drag-n-drop/types/selectable-open-event';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { FormChangesModalsService } from '@modules/common/services/form-changes-modals.service';
import { from } from 'rxjs';
import { FormsSidebarActionsService } from '../services/forms-sidebar-actions.service';
import { ARCHIVE_EMPTY_TITLE, NO_MATCH_TITLE, TRASH_EMPTY_TITLE } from '@modules/common/components/list/base-stitch-list.component';
import { ListItemSeparatorComponent } from '@modules/common/components/list/list-items-separator/list-items-separator.component';
import { toDisplayZoneWallClock } from '@modules/full-calendar/types/display-timezone-format';
import { toListItems } from './forms-list.util';
import { FormsSidebarContextMenuDirective } from '../menus/forms-sidebar-context-menu.directive';
export const FORMS_VIEW_TITLES: Record<FormsView, string> = {
  all: 'All forms',
  recent: 'Recent',
  flagged: 'Flagged',
  followup: 'Follow Up',
  snoozed: 'Snoozed',
  templates: 'Templates',
  archived: 'Archive',
  deleted: 'Trash',
  search_results: 'All Results',
};

@Component({
  selector: 'app-forms-layout',
  host: { '[class.forms-panel]': 'panel()' },
  animations: [heightAnimation],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Pagination2Component, ListItemSeparatorComponent, ListSelectionBarComponent, ListLoadErrorComponent, AppCommonModule, NavAddMenuComponent, ContextMenuStateIndicatorComponent, PopoverDirective, NoContentStubComponent, LoadingIndicatorComponent, FormsLibraryCardComponent, FormsHubListComponent, FormsFolderLibraryCardComponent, FormFolderContextMenuDirective, FormsLibraryOptionsDirective, FormsQuickFormComponent, FormsSearchPopoverComponent, SearchStateIndicatorComponent, QuickActionButtonComponent,
    CommonModule,
    MatTooltipModule,
    Button2Component,
    IconComponent,
    SidebarItemComponent,
    ElementsModule,
    CollapsibleRailShellModule,
    SplitViewModule,
    DragNDropModule,
    FormsRecordComponent, PanelFormCreateComponent, FormsViewsContextMenuDirective,
    FormsFolderRecordComponent, FormsSidebarContextMenuDirective,
    UtilsModule,
  ],
  providers: [
    FormsWorkspaceStore,
    FormsUiSession,
    FormsStitchyService,
    FormsSidebarActionsService,
    FormsPanelStateService,
    {
      provide: BaseAppStateService,
      useFactory: (store: FormsWorkspaceStore) => ({
        setTeam: () => undefined,
        setVirtualFolder: (folder: string) => {
          if (folder === 'search_results') void store.landOnSearchResults();
        },
      }),
      deps: [FormsWorkspaceStore],
    },
    { provide: SEARCH_MY_SCOPE_LABEL, useValue: 'Search My workspace' },
  ],
  templateUrl: './forms-layout.component.html',
  styleUrl: './forms-layout.component.less',
})
export class FormsLayoutComponent {
  public readonly sidebarActions = inject(FormsSidebarActionsService);
  readonly panel = input(false);
  readonly store = inject(FormsWorkspaceStore);
  private readonly toaster = inject(ToasterService);
  readonly app = Application.forms;
  readonly SidebarSplitViewKey = SidebarSplitViewKey;
  readonly fullWindow = inject(FullWindowService);
  readonly views: readonly {
    id: Exclude<FormsView, 'search_results'>;
    title: string;
    icon: import('@frontendlib/common/providers/icons/icons').Icon;
  }[] = [
    { id: 'all', title: 'All forms', icon: 'sapphire-form' },
    { id: 'recent', title: 'Recent', icon: 'sapphire-recent' },
    { id: 'flagged', title: 'Flagged', icon: 'sapphire-flagged' },
    { id: 'followup', title: 'Follow Up', icon: 'sapphire-follow-up' },
    { id: 'snoozed', title: 'Snoozed', icon: 'sapphire-snooze' },
    { id: 'templates', title: 'Templates', icon: 'sapphire-template' },
    { id: 'archived', title: 'Archive', icon: 'sapphire-archive' },
    { id: 'deleted', title: 'Trash', icon: 'sapphire-trash' },
  ];
  readonly rows = computed(() => this.store.items().map((data, position) => ({ data, position, identifier: data.id })));
  protected readonly TRASH_EMPTY_TITLE = TRASH_EMPTY_TITLE;
  protected readonly ARCHIVE_EMPTY_TITLE = ARCHIVE_EMPTY_TITLE;
  protected readonly NO_MATCH_TITLE = NO_MATCH_TITLE;
  protected readonly items = computed(() => {
    const timeZone = this.displayTimeZone.timeZone();
    return toListItems(this.store.items(), this.store.options(), (date) => toDisplayZoneWallClock(date, timeZone));
  });
  readonly listFolded = signal(false);
  readonly railFolded = computed(() => !this.panel() && this.fullWindow.railFoldedApp() === this.app);
  readonly navigation = inject(FormsPanelNavigationService);
  private readonly handoff = inject(FormsMainHandoffService);
  private readonly splitView = inject(SplitViewService);
  private readonly savedListMinimized = toSignal(this.splitView.getMinimized('formsList'), { initialValue: false });
  readonly listMinimized = computed(() => this.listFolded() || this.savedListMinimized());
  readonly editorListOpen = signal(false);
  readonly editorListMinimized = computed(() => !this.editorListOpen() || this.editorListSaved());
  private readonly editorListSaved = toSignal(this.splitView.getMinimized('formsEditorList'), { initialValue: false });
  /** Icon-only rail state of this sidebar's own split-view pane — same key `app-sidebar-knots`/`app-sidebar-tags` already read. */
  readonly minimized = toSignal(this.splitView.getMinimized(SidebarSplitViewKey.forms), { initialValue: false });
  private readonly foldersExpandedStorage = new LocalStorageItem<boolean>('forms-sidebar.folders-expanded', {
    default: true,
    parse: ({ raw }) => (raw === false || raw === 'false' ? false : raw === true || raw === 'true' ? true : true),
  });
  readonly foldersExpanded = toSignal(this.foldersExpandedStorage.get().pipe(map((value) => value ?? true)), {
    initialValue: this.foldersExpandedStorage.getSync() ?? true,
  });
  setFoldersExpanded(expanded: boolean): void {
    this.foldersExpandedStorage.set(expanded);
  }
  readonly viewsMenuOpened = signal(false);
  readonly panelFolderFormOpen = signal(false);
  readonly panelParentFolder = signal<FormFolderSummaryDto | null>(null);
  readonly panelSearchClose = new Subject<void>();
  protected readonly resetSelected = new Subject<void>();
  readonly panelCreationAllowed = computed(() => !this.store.foldersOpen() && !this.store.selectedFolder() && this.store.view() !== 'search_results' && (this.store.folder() !== null || this.store.view() === 'all' || this.store.view() === 'recent'));
  readonly panelFolderCreationAllowed = computed(() => this.store.foldersOpen() && !['archived', 'deleted'].includes(this.store.view()));
  readonly panelSearchApplied = computed(() => !!this.store.search().trim() || !!this.sharedSearch()?.isApplied());
  readonly panelCanGoBack = computed(() => this.dp?.canGoBack() ?? false);
  readonly panelBackLabel = computed(() => { const title = this.dp?.peekHistory()?.title; return title ? 'Back to ' + title : 'Go back'; });
  readonly mayApplyPanelSearch = (): Promise<boolean> => this.beforeLeave();
  readonly applyPanelSearch = async (query: string): Promise<boolean> => {
    if (!(await this.store.close())) return false;
    this.panelParentFolder.set(null); this.store.search.set(this.store.foldersOpen() ? query : ''); this.store.offset.set(0); this.store.listAsOf.set(new Date().toISOString());
    if (!this.store.foldersOpen()) { this.store.view.set(query ? 'search_results' : 'all'); this.store.folder.set(null); }
    if (!this.store.foldersOpen()) this.searchService.search(query);
    await this.store.reload(); return true;
  };
  readonly folderQuickClose = new Subject<void>();
  readonly folderCreating = signal(false);
  readonly creationMenuClose = new Subject<void>();
  private readonly nativeForms = inject(FormsStitchService);
  private readonly injector = inject(Injector);
  private readonly displayTimeZone = inject(DisplayTimeZoneService);
  private readonly searchService = inject(AdvancedSearchService);
  private readonly sharedSearch = toSignal(this.searchService.getState(), { initialValue: this.searchService.getStateSync() });
  readonly hasSearchResults = computed(() => {
    const state = this.sharedSearch(), local = state?.[Application.forms];
    return !!state?.isApplied() || !!local?.dateRangeFrom || !!local?.dateRangeTo;
  });
  readonly listTitle = computed(() => this.store.foldersOpen() ? 'Folders' : this.store.selectedFolder()?.title || (this.store.current() ? this.panelParentFolder()?.title : undefined) || this.store.folders().find(folder => folder.id === this.store.folder())?.title || FORMS_VIEW_TITLES[this.store.view()]);
  protected readonly pageSize = Constants.PAGE_SIZE;
  readonly pageNumber = computed(() => Math.floor(this.store.offset() / this.pageSize) + 1);
  protected readonly visibleFolders = computed(() => this.store.sortedFolders().filter(folder =>
    this.store.view() === 'deleted' ? folder.deleted : this.store.view() === 'archived' ? folder.archived && !folder.deleted : !folder.archived && !folder.deleted));
  readonly folderCount = computed(() => this.store.folderCount());
  readonly folderRows = computed(() => this.store.folderItems());
  readonly createFolderTask = (title: string): Observable<FormFolderSummaryDto> => from(this.createFolder(title));

  readonly beforeCreateForm = (initiator?: PanelFormCreateComponent): Promise<boolean> => this.beforeLeave(initiator);
  async createdForm(item: NativeForm): Promise<void> {
    const tenant = this.store.space.contextId(), generation = this.store.space.contextChanges();
    this.store.ensureVisibleAfterCreate();
    const opened = await this.open(item, !this.panel());
    await this.store.refresh();
    if (!opened && !this.destroyRef.destroyed && tenant === this.store.space.contextId() && generation === this.store.space.contextChanges())
      this.injector.get(ToasterService).show({ text: 'Form created', actions: [{ text: 'Open', handler: () => {
        if (!this.destroyRef.destroyed && tenant === this.store.space.contextId() && generation === this.store.space.contextChanges()) void this.open(item, !this.panel());
      } }] });
  }

  private readonly selectableList = viewChild(SelectableListDirective<FormSummaryDto>);
  private readonly filterKey = computed((): readonly unknown[] => [this.store.view(), this.store.folder(), this.store.options(), this.store.search(), this.store.offset(), this.store.foldersOpen()]);
  private listed: { readonly key: readonly unknown[]; readonly ids: ReadonlySet<string> } | null = null;
  private readonly railShell = viewChild(CollapsibleRailShellComponent);
  private readonly record = viewChild(FormsRecordComponent);
  private readonly folderRecord = viewChild(FormsFolderRecordComponent);
  private readonly stitchy = inject(FormsStitchyService);
  private readonly scope = inject(APPLICATION_SCOPE_TOKEN, { optional: true });
  private readonly knowledge = inject(FormKnowledgeVisibilityService);
  private readonly dp = inject(DynamicPanelService, { optional: true });
  private readonly showsItem = computed(() => !!this.store.current() || !!this.store.selectedFolder());
  private readonly route = inject(ActivatedRoute);
  private readonly routeParams = toSignal(this.route.paramMap);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formsChanges = inject(FormChangesModalsService);
  private readonly draft = new FormGroup({ pending: new FormControl('') });
  private readonly draftKey = 'forms-' + crypto.randomUUID();
  private timer: ReturnType<typeof setTimeout> | undefined;
  private readonly panelState = inject(FormsPanelStateService);
  private lastSpace = this.store.space.contextId();
  private deepLink = '';
  private panelTargetRequest = '';
  /** BoardsPanel captures the focused library row before opening and restores it after Back. */
  private returnFocus: HTMLElement | null = null;
  constructor() {
    effect(() => this.announceRemovals());
    effect(() => this.selectPreviewed());
    this.destroyRef.onDestroy(this.stitchy.registerHost(formsUiHost({
      store: this.store, record: () => this.record(), folderRecord: () => this.folderRecord(), scope: this.scope, knowledge: this.knowledge,
      panels: () => ({ rail: !this.panel() && !(this.railShell()?.shownParked() ?? false) && !this.railFolded(),
        list: !this.listFolded() && !this.splitView.isMinimized('formsList'),
        dp: !this.splitView.isMinimized('dp') && !this.fullWindow.dpFolded() }),
      setPanel: (panel, open) => {
        if (panel === 'rail') {
          const rail = this.railShell();
          if (!rail) throw Error('This Forms surface has no navigation rail.');
          if (this.railFolded()) { if (open) this.fullWindow.reopenRail(); return; }
          if (rail.shownParked() && open) rail.dock();
          else if (!rail.shownParked() && !open) rail.park();
          return;
        }
        if (panel === 'list') { if (this.panel()) throw Error('The Forms fourth panel has no separate list pane.'); this.listFolded.set(!open); }
        const key = panel === 'list' ? 'formsList' : 'dp';
        if (open) this.splitView.reopen(key, panel === 'list' ? 300 : 250);
        else { this.splitView.collapsed.set(true, key); this.splitView.emitSizeChanges(); }
        if (panel === 'dp' && open) this.fullWindow.reopenDp();
      },
      resizePanel: (panel, width) => {
        if (panel === 'list' && this.panel()) throw Error('The Forms fourth panel has no separate list pane.');
        const key = panel === 'list' ? 'formsList' : 'dp';
        this.splitView.size.set(width, key);
        this.splitView.collapsed.set(width < (panel === 'list' ? 300 : 250), key);
        this.splitView.emitSizeChanges();
      },
      close: this.closeRecordSurface,
      openForm: uid=>this.open({id:uid,accountId:this.store.space.contextId()||''},!this.panel()),
    })));
    effect((onCleanup) => {
      if (this.panel() && this.dp) onCleanup(this.dp.registerSurfaceGuard(() => of(this.store.canLeaveRecord())));
    });
    effect((onCleanup) => {
      if (!this.panel() || !this.showsItem()) return;
      const release = untracked(() => this.dp?.holdPanelItem());
      onCleanup(() => release?.());
    });
    effect(()=>{if(this.panel()&&!this.store.current()&&!this.store.selectedFolder())untracked(()=>this.panelParentFolder.set(null));});
    effect(() => {
      this.store.offset();
      this.store.view();
      this.store.folder();
      this.store.search();
      this.store.options();
      this.resetSelected.next();
    });
    // Navigation persistence (Slides' `SlidesPanelStateService` use): the open board is written as it changes, Folders mode is restored
    // on the panel's OWN list store (never the main view's) and written back; a space switch restores that space's Folders mode.
    effect(() => {
      if (!this.panel()) return;
      const id = this.store.current()?.id ?? null;
      untracked(() => this.panelState.open(id));
    });
    effect(() => {
      if (!this.panel()) return;
      const open = this.store.foldersOpen();
      const space = this.store.space.contextId();
      untracked(() => {
        if (space !== this.lastSpace) {
          this.lastSpace = space;
          if (this.panelState.state().foldersOpen) void this.store.showFolders();
          return;
        }
        this.panelState.showFolders(open);
      });
    });
    const main = isMainApplicationScope();
    effect(() => {
      this.fullWindow.starts();
      if (main && this.fullWindow.activeApp() === this.app) untracked(() => { this.listFolded.set(true); this.editorListOpen.set(false); });
    });
    effect(() => {
      if (main && (!this.store.current() || !this.store.editor()))
        untracked(() => {
          this.fullWindow.end(this.app);
          this.listFolded.set(false);
          this.editorListOpen.set(false);
        });
    });
    if (main) this.destroyRef.onDestroy(this.fullWindow.attach(this.app));
    effect(() => {
      const request = this.handoff.pending();
      if (main && request) untracked(() => void this.applyHandoff(request));
    });
    this.destroyRef.onDestroy(() => {
      if (main) { this.fullWindow.end(this.app); this.handoff.withdrawPending(); }
      if (this.timer) clearTimeout(this.timer);
      this.formsChanges.unregisterForm(this.draftKey);
    });

    effect(() => {
      if (this.store.dirty()) this.draft.markAsDirty();
      else this.draft.markAsPristine();
    });
    effect(() => {
      const id = this.routeParams()?.get('id'),
        tenant = this.store.space.contextId();
      if (!this.panel() && id && tenant && this.deepLink !== tenant + id) {
        this.deepLink = tenant + id;
        untracked(() => {
          if (this.route.snapshot.data['kind'] === 'folder') void this.store.openFolderId(id, tenant);
          else void this.store.open({ id, accountId: tenant }, true);
        });
      }
    });
    effect(() => {
      const target = this.navigation.target();
      if (this.panel() && target && target.tenant !== this.store.space.contextId()) {
        untracked(() => this.navigation.consume(target));
        return;
      }
      if (this.panel() && target && target.tenant === this.store.space.contextId()) {
        untracked(() => {
          this.panelTargetRequest=target.request;const generation=this.store.space.contextChanges();
          const current=():boolean=>!this.destroyRef.destroyed&&this.panelTargetRequest===target.request&&this.store.space.contextChanges()===generation&&this.store.space.contextId()===target.tenant;
          if (target.kind === 'form') void this.store.open({ id: target.id, accountId: target.tenant }, false,current).then(opened=>{if(opened&&current()&&target.tab)this.store.recordRequest.set({tab:target.tab,request:target.request});});
          else void this.store.openFolderId(target.id, target.tenant,current);
          this.navigation.consume(target);
        });
      }
    });
  }
  private async applyHandoff(request: FormsHandoff): Promise<void> {
    let generation = this.store.space.contextChanges(), shown = false, reason: FormsHandoffReason = 'failed';
    const current = (): boolean => !this.destroyRef.destroyed && this.handoff.isPending(request) && this.store.space.contextChanges() === generation;
    try {
      if (request.spaceType !== undefined && (this.store.space.type() !== request.spaceType || this.store.space.contextId() !== request.spaceId)) {
        const switched = await firstValueFrom(this.store.space.setContext(request.spaceType, request.spaceId), { defaultValue: false });
        generation = this.store.space.contextChanges();
        if (!current() || !switched || this.store.space.contextId() !== request.spaceId) { reason = current() ? 'vetoed' : 'superseded'; return; }
      }
      if (!current() || (request.spaceId && this.store.space.contextId() !== request.spaceId)) { reason = 'unavailable'; return; }
      const tenant = request.spaceId || this.store.space.contextId();
      if (request.kind === 'folder') {
        await this.store.openFolderId(request.id, tenant, current);
        shown = current() && this.store.selectedFolder()?.id === request.id && this.store.selectedFolder()?.accountId === tenant;
      } else {
        shown = await this.store.open({ id: request.id, accountId: tenant }, request.intent === 'editor', current);
        if (shown && current() && request.intent === 'editor') {
          this.fullWindow.begin(this.app); this.listFolded.set(true);
          if (request.editorCommand) {
            const record = await this.readyHandoffRecord(request, current);
            if (!record || !current()) { shown = false; reason = 'superseded'; return; }
            const handle = await record.waitForEditor();
            if (!current()) { shown = false; reason = 'superseded'; return; }
            const outcome = await handle.executeUi(request.editorCommand);
            if (!current()) { shown = false; reason = 'superseded'; return; }
            request.onEditorResult?.(outcome); shown = outcome.ok;
          }
        }
        if (shown && current() && request.tab) this.store.recordRequest.set({ tab: request.tab, request: crypto.randomUUID() });
      }
      reason = shown ? 'shown' : current() ? 'vetoed' : 'superseded';
    } catch (error) { if (current()) this.store.fail(error, 'Could not open this form. Open it again to retry.'); }
    finally { this.handoff.clear(request, shown && current(), !current() ? 'superseded' : reason === 'shown' ? 'failed' : reason); }
  }
  private readyHandoffRecord(request: FormsHandoff, current: () => boolean): Promise<FormsRecordComponent | undefined> {
    const ready = (): FormsRecordComponent | undefined => this.store.current()?.id === request.id ? this.record() : undefined;
    if (current() && ready()) return Promise.resolve(ready());
    return new Promise(resolve => {
      const timer = setTimeout(() => { watcher.destroy(); resolve(undefined); }, 15_000);
      const watcher = effect(() => {
        this.handoff.pending(); this.store.space.contextChanges(); const record = this.record();
        if (!current() || (record && ready())) { clearTimeout(timer); watcher.destroy(); resolve(current() ? record : undefined); }
      }, { injector: this.injector });
    });
  }
  async beforeLeave(initiator?: PanelFormCreateComponent): Promise<boolean> {
    return this.store.beforeLeave(false, initiator?.activePreflightGuardKey);
  }
  search(value: string): void {
    this.store.search.set(value);
    this.store.offset.set(0);
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.store.reload(), 250);
  }
  protected searchFolders(event: Event): void {
    if (event.target instanceof HTMLInputElement) this.search(event.target.value);
  }
  async create(): Promise<void> {
    if (!(await this.beforeLeave())) return;
    if (this.injector.get(AccessLimitGateService).blocks('createItem', this.injector)) return;
    this.creationMenuClose.next();
    await this.store.create();
    if (this.store.current() && this.panel()) this.store.editor.set(false);
    if (this.store.current() && !this.panel()) {
      this.fullWindow.begin(this.app);
      this.listFolded.set(true);
    }
  }
  async open(item: Pick<FormSummaryDto,'id'|'accountId'>, editor = false): Promise<boolean> {
    if (editor && this.panel()) {
      if (await this.beforeLeave()) return this.handoff.handOff({ kind: 'form', id: item.id, intent: 'editor', spaceType: this.store.space.type(), spaceId: item.accountId });
      return false;
    }
    if (!this.store.canLeaveRecord()) return false;
    const returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const parent = this.panel() ? this.store.selectedFolder() : null;
    const opened=await this.store.open(item, editor);
    if (opened) {
      if (this.panel()) { this.panelParentFolder.set(parent); this.returnFocus = returnFocus; }
      if (editor && !this.panel()) {
      this.fullWindow.begin(this.app);
      this.listFolded.set(true);
      }
    }
    return opened;
  }
  selected(event: SelectableOpenEvent<FormSummaryDto>): void {
    if (!(this.panel() && event.source === 'pointer') && event.item?.data) void this.open(event.item.data);
  }
  drag(item: FormSummaryDto): DragData {
    return { type: DragDataTypes.form, data: [item] };
  }
  async page(delta: number): Promise<void> {
    this.store.offset.update((value) => Math.max(0, value + this.pageSize * delta));
    await this.store.reload();
  }
  readonly folderSelectAllRequest = signal(0);
  private folderSelectionSequence = 0;
  readonly folderContentsFilters = computed<Partial<FormStitchFilters>>(() => {
    const folder = this.store.selectedFolder();
    return { folderUid: folder?.id, deleted: !!folder?.deleted, ...(!folder?.deleted ? { archived: !!folder?.archived } : {}), sortBy: 'createdAt' };
  });
  public selectFolderContents(): void { this.folderSelectAllRequest.set(++this.folderSelectionSequence); }
  public showCreatedFolder(folder: FormFolderSummaryDto): void {
    if (this.store.editor()) void this.store.closeEditor();
    this.setFoldersExpanded(true);
    this.store.setFolder(folder);
  }
  public async openFolder(folder: FormFolderSummaryDto, selectAll = false, fromFoldersLibrary = false): Promise<void> {
    if (!this.store.canLeaveRecord()) return;
    const returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    await this.store.openFolder(folder, fromFoldersLibrary && !this.panel());
    if (this.panel() && this.store.selectedFolder()?.id === folder.id) this.returnFocus = returnFocus;
    if (this.panel() && this.store.selectedFolder()?.id === folder.id) this.panelParentFolder.set(null);
    if (this.store.selectedFolder()?.id === folder.id && this.store.space.contextId() === folder.accountId)
      this.folderSelectAllRequest.set(selectAll ? ++this.folderSelectionSequence : 0);
  }
  public async openFolderForm(id: string, editor = false): Promise<void> {
    const folder = this.store.selectedFolder();
    if (folder && this.panel()) {
      if (editor) { if (await this.beforeLeave()) await this.handoff.handOff({ kind: 'form', id, intent: 'editor', spaceType: this.store.space.type(), spaceId: folder.accountId }); }
      else if (await this.store.open({ id, accountId: folder.accountId }, false)) this.panelParentFolder.set(folder);
      return;
    }
    if (folder) {
      if ((await this.store.open({ id, accountId: folder.accountId }, editor)) && editor && !this.panel()) {
        this.fullWindow.begin(this.app); this.listFolded.set(true);
      }
    }
  }
  public readonly navigateCollection = async (view: FormsView | 'folders'): Promise<void> => {
    if (!this.store.canLeaveRecord()) return;
    if (view === 'folders') await this.store.showFolders(); else await this.store.selectView(view);
    this.panelParentFolder.set(null);
  };
  public readonly closeRecordSurface = async (): Promise<boolean> => this.panel() ? this.panelBack() : this.store.close();
  public async panelBack(): Promise<boolean> {
    if (!this.store.canLeaveRecord()) return false;
    const folder = this.panelParentFolder(), selectedFolder = this.store.selectedFolder();
    if (!(await this.store.close())) return false;
    this.panelParentFolder.set(null);
    if (folder) await this.store.openFolder(folder);
    else if (selectedFolder) await this.store.showFolders();
    const returnFocus = this.returnFocus, generation = this.store.documentGeneration();
    requestAnimationFrame(() => {
      if (!this.destroyRef.destroyed && generation === this.store.documentGeneration() && returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
    });
    return true;
  }
  public async clearPanelSearch(): Promise<void> {
    if (!(await this.beforeLeave())) return;
    this.searchService.resetSearchTerms(); await this.applyPanelSearch('');
  }
  private selectPreviewed(): void {
    const id = this.store.current()?.id;
    const list = this.selectableList();
    const position = id ? this.store.items().findIndex((form) => form.id === id) : -1;
    if (!id || !list || position < 0) return;
    untracked(() => {
      const service = list.selectableService;
      if (service.getSelectedItemsSync().some((item) => item.identifier === id)) return;
      service.setSelectedItems([{ data: this.store.items()[position], position, identifier: id }]);
    });
  }
  private announceRemovals(): void {
    const key = this.filterKey();
    const ids = new Set(this.store.items().map((form) => form.id));
    const list = this.selectableList();
    untracked(() => {
      const previous = this.listed;
      this.listed = { key, ids };
      if (!previous || previous.key !== key) return;
      const gone = [...previous.ids].filter((id) => !ids.has(id));
      if (!gone.length || this.store.editor()) return;
      list?.selectableService.expectRemoval(gone);
      const previewed = this.store.current()?.id;
      if (previewed && gone.includes(previewed) && ids.size === 0) void this.store.close();
    });
  }
  public goBackInPanel(): void { if (!this.store.canLeaveRecord()) return; this.dp?.goBack(); }
  async goToPage(page: number): Promise<void> {
    this.store.offset.set(Math.max(0, page - 1) * this.pageSize); await this.store.reload();
  }
  async newFolder(): Promise<void> { await this.createFolder('Untitled folder').catch(() => undefined); }
  private async createFolder(title: string): Promise<FormFolderSummaryDto> {
    if (this.folderCreating()) throw Error('Wait for the folder creation to finish.');
    if (this.injector.get(AccessLimitGateService).blocks('createItem', this.injector) || !(await this.store.beforeLeave())) throw Error('The folder was not created.');
    const tenant = this.store.space.contextId(); if (!tenant) throw Error('Select a workspace.');
    const generation = this.store.space.contextChanges();
    this.folderCreating.set(true);
    try {
      const folder = await this.store.api.createFolder(tenant, title);
      if (this.store.space.contextId() === tenant && this.store.space.contextChanges() === generation) { void this.store.refreshFolders(); await this.store.reload(); if (!this.panel()) this.showCreatedFolder(folder); }
      return folder;
    } catch (error) {
      this.store.fail(error, 'Failed to create the folder.'); throw error;
    } finally { this.folderCreating.set(false); }
  }
  async createOptions(mode: 'template' | 'import'): Promise<void> {
    if (this.injector.get(AccessLimitGateService).blocks('createItem', this.injector)) return;
    this.creationMenuClose.next();
    if (!(await this.store.beforeLeave())) return;
    this.injector.get(BoardsDialogService).showModal({ component: FormsCreationPickerComponent, injector: this.injector, title: mode === 'template' ? 'From a template' : 'Import a form',
      content: { mode, folderId: this.store.folder(), onCreated: async (item: FormSummaryDto) => { await this.open(item, !this.panel()); return this.store.current()?.id === item.id; } } });
  }
  private movableForms(data: DragData): FormSummaryDto[] {
    const tenant = this.store.space.contextId();
    if (data.type !== DragDataTypes.form || !data.data.length || data.data.length > 50 || JSON.stringify(data.data).length > 65_536) return [];
    const valid = (item: (typeof data.data)[number]): item is FormSummaryDto => 'itemType' in item && item.itemType === StitchType.FORM && item.accountId === tenant && typeof item.id === 'string' && !!item.id && typeof item.deleted === 'boolean' && 'headVersion' in item && typeof item.headVersion === 'number';
    if (!data.data.every(valid)) return [];
    return [...new Map(data.data.filter(valid).map(item => [item.id, item])).values()];
  }
  readonly canMoveForms = (data: DragData): boolean => !this.store.busy() && this.movableForms(data).length > 0;
  async moveToFolder(data: DragData, folder: FormFolderSummaryDto): Promise<void> {
    if (folder.accountId !== this.store.space.contextId() || folder.deleted || folder.archived) return;
    await this.triage(data, { folderUid: folder.id, deleted: false, archived: false });
  }
  async dropOnView(data: DragData, view: FormsView): Promise<void> {
    const tomorrow = relativeDateToWorkDayStart('tomorrow', this.displayTimeZone.timeZone());
    const changes: BunchUpdateOptions & { folderUid?: string | null; isTemplate?: boolean } = view === 'archived' ? { archived: true, deleted: false } : view === 'deleted' ? { deleted: true } : {
      deleted: false, archived: false,
      ...(view === 'flagged' ? { flagged: true } : view === 'templates' ? { isTemplate: true } : view === 'followup' ? { followed: tomorrow } : view === 'snoozed' ? { snoozed: tomorrow } : { folderUid: null }),
    };
    const restores = view === 'flagged' || view === 'templates' || view === 'followup' || view === 'snoozed';
    await this.triage(data, changes, (form) => (restores && (form.deleted || form.archived)) || !(view === 'flagged' ? form.flagged : view === 'archived' ? form.archived : view === 'deleted' ? form.deleted : view === 'templates' ? form.isTemplate : view === 'followup' ? form.followed : view === 'snoozed' ? form.snoozed : false));
  }
  private async triage(data: DragData, changes: BunchUpdateOptions & { folderUid?: string | null; isTemplate?: boolean }, keep: (form: FormSummaryDto) => boolean = () => true): Promise<void> {
    if (!this.canMoveForms(data) || !(await this.store.beforeLeave())) return;
    const items = this.movableForms(data).filter(keep); if (!items.length) return;
    try { await firstValueFrom(this.nativeForms.bunchUpdate({ ids: items.map(item => item.id) }, changes, { tenantItems: items })); await this.store.reload(); }
    catch (error) { this.store.fail(error); }
  }
}
